/**
 * 全国态势地图 · 投影与几何
 *
 * 从原型 `prototype/js/app.js` 的 `initProjection` / `project` / `geoPath` 移植。
 *
 * 设计说明（保持原型的 SVG 自绘方案，不引入在线瓦片）：
 *   - 中国范围约 73–136°E, 17–55°N
 *   - 墨卡托 y 必须换算成「度」量纲（×180/π），否则与经度跨度不同量纲，
 *     会把地图纵向压扁成一条线。
 *   - 本文件是纯函数，便于单元自测（scripts/verify-map.mjs）。
 */

/** [经度, 纬度] */
export type Position = [number, number];

export interface PolygonGeometry {
  type: 'Polygon';
  coordinates: Position[][];
}

export interface MultiPolygonGeometry {
  type: 'MultiPolygon';
  coordinates: Position[][][];
}

export type ChinaGeometry = PolygonGeometry | MultiPolygonGeometry;

export interface ChinaFeatureProperties {
  adcode: number | string;
  name: string;
  /** 省份中心点（label 定位用） */
  center?: Position;
  centroid?: Position;
  level?: string;
  childrenNum?: number;
}

export interface ChinaFeature {
  type: 'Feature';
  properties: ChinaFeatureProperties;
  geometry: ChinaGeometry;
}

export interface ChinaGeo {
  type: 'FeatureCollection';
  features: ChinaFeature[];
}

/** 投影上下文 */
export interface Projection {
  w: number;
  h: number;
  scale: number;
  ox: number;
  oy: number;
  lon0: number;
  /** mercatorY(55) —— 用于把纬度转成屏幕 y */
  y1: number;
}

const LON0 = 73;
const LON1 = 136;
const LAT0 = 17;
const LAT1 = 55;
const PAD = 28;

/** 纬度 → 墨卡托纵坐标（度量纲） */
export function mercatorY(lat: number): number {
  return (Math.log(Math.tan(Math.PI / 4 + ((lat * Math.PI) / 180) / 2)) * 180) / Math.PI;
}

/** 依据画布尺寸初始化投影（等比例居中，四周留白） */
export function createProjection(w: number, h: number): Projection {
  const y0 = mercatorY(LAT0);
  const y1 = mercatorY(LAT1);
  const gw = LON1 - LON0;
  const gh = y1 - y0;
  const sx = (w - PAD * 2) / gw;
  const sy = (h - PAD * 2) / gh;
  const scale = Math.max(0.0001, Math.min(sx, sy));
  return {
    w,
    h,
    scale,
    ox: (w - gw * scale) / 2,
    oy: (h - gh * scale) / 2,
    lon0: LON0,
    y1,
  };
}

/** 经纬度 → 屏幕坐标 */
export function project(p: Projection, lng: number, lat: number): [number, number] {
  const x = p.ox + (lng - p.lon0) * p.scale;
  const y = p.oy + (p.y1 - mercatorY(lat)) * p.scale;
  return [x, y];
}

/** GeoJSON 几何 → SVG path 的 d 属性 */
export function geometryToPath(p: Projection, geom: ChinaGeometry): string {
  const polygons: Position[][][] =
    geom.type === 'Polygon' ? [geom.coordinates] : geom.coordinates;

  let d = '';
  for (const polygon of polygons) {
    for (const ring of polygon) {
      ring.forEach(([lng, lat], i) => {
        const [x, y] = project(p, lng, lat);
        d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
      });
      d += 'Z';
    }
  }
  return d;
}

/** 是否省级行政区（adcode 以 0000 结尾），排除南海诸岛等特殊要素 */
export function isProvinceFeature(f: ChinaFeature): boolean {
  return /^\d{2}0000$/.test(String(f.properties.adcode ?? ''));
}

/** 省份简称（去掉「省 / 市 / 自治区 / 民族」等后缀） */
export function shortProvinceName(name: string): string {
  return name.replace(/省|市|自治区|特别行政区|维吾尔|回族|壮族/g, '');
}
